import { useEffect, useRef } from 'react';
import { useGameStore } from '../store/gameStore';

/**
 * In-game tracker for today's daily challenge. Lives outside the engine
 * (per the requirement to keep challenge logic out of GameEngine) — it
 * watches the HUD state that the engine already pushes and translates
 * relevant transitions into progress updates.
 *
 * Tracking by challenge kind:
 *   reach_mass   — push `mass` directly; store keeps monotonic max.
 *   chain_combo  — push `combo` size on every increment.
 *   kill_rival   — count edge transitions of `killedByName` from null→set
 *                  during a single life (the engine pushes killedByName
 *                  every time YOU kill a NAMED rival via the kill toast
 *                  path); we sum across the day.
 *   survive_time — runs a 1-second interval while `alive`, increments
 *                  a session counter. Resets on death; pushes the max
 *                  seen this session.
 *
 * The hook is stateless from React's perspective — refs hold the prior
 * values to compute edges/deltas without re-renders.
 */
export function useDailyChallengeTracker(): void {
  const challenge = useGameStore((s) => s.dailyChallenge);
  const updateProgress = useGameStore((s) => s.updateChallengeProgress);
  const mass = useGameStore((s) => s.hud.mass);
  const combo = useGameStore((s) => s.hud.combo);
  const kills = useGameStore((s) => s.hud.kills);
  const alive = useGameStore((s) => s.hud.alive);

  // Per-day cumulative counter for kill_rival challenges. The store's
  // monotonic update means we only need to push the running max.
  const rivalKillsTodayRef = useRef<number>(0);
  const lastKillsRef = useRef<number>(0);
  // Survive timer state — only meaningful when alive.
  const aliveSecondsRef = useRef<number>(0);

  // Mass: always push for reach_mass challenges.
  useEffect(() => {
    if (challenge.kind !== 'reach_mass') return;
    if (mass <= 0) return;
    updateProgress(mass);
  }, [challenge.kind, mass, updateProgress]);

  // Combo: push the current combo size on each step.
  useEffect(() => {
    if (challenge.kind !== 'chain_combo') return;
    if (combo <= 0) return;
    updateProgress(combo);
  }, [challenge.kind, combo, updateProgress]);

  // Kills: detect increments and accumulate across the day. The HUD
  // doesn't currently distinguish rival kills from filler-bot kills in
  // the kills counter (engine increments for both), so for now we count
  // all kills — close enough for v1, and we can tighten later if it
  // matters.
  useEffect(() => {
    if (challenge.kind !== 'kill_rival') return;
    if (kills > lastKillsRef.current) {
      const delta = kills - lastKillsRef.current;
      rivalKillsTodayRef.current += delta;
      updateProgress(rivalKillsTodayRef.current);
    }
    lastKillsRef.current = kills;
  }, [challenge.kind, kills, updateProgress]);

  // Reset kills baseline on respawn so a new life doesn't count the
  // entire previous life over again.
  useEffect(() => {
    if (alive) {
      lastKillsRef.current = 0;
    }
  }, [alive]);

  // Survive timer — only runs while alive and only for the survive_time
  // challenge. 1Hz tick keeps it cheap and accurate enough for a 60s goal.
  useEffect(() => {
    if (challenge.kind !== 'survive_time') return;
    if (!alive) {
      aliveSecondsRef.current = 0;
      return;
    }
    aliveSecondsRef.current = 0;
    const handle = window.setInterval(() => {
      aliveSecondsRef.current += 1;
      updateProgress(aliveSecondsRef.current);
    }, 1000);
    return () => window.clearInterval(handle);
  }, [challenge.kind, alive, updateProgress]);
}
